import { useEffect, useRef, useState } from 'react';
import { VideoJsPlayer } from 'video.js';
import { AbsoluteCenter, BigBtnFloatingButton, BigBtnFloatingContainer, BigPlayBtnFloatingButton } from '../styles';
import { IconFastRewind, IconPause, IconPlayBig, IconPlayForward, IconReplay } from '../../../icons';
import { Sources } from '../PlayerModels';
import { CONTENT_END_EVENTS, PLAY_STATE_EVENTS } from '../PlayerServices';
import UsePlayerProps from '../../../Store/usePlayerProps';

const PlayControls = ({ player, isVast = false, isShort = false }:
  { player: VideoJsPlayer; isVast?: boolean; isShort?: boolean }) => {
  const [playState, setPlayState] = useState<'play' | 'pause' | 'ended'>(player && player?.paused() ? 'pause' : 'play');
  const [isSeeking, setIsSeeking] = useState(false);
  const time = useRef<any>(null);
  const hasEnded = useRef<boolean>(false);
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { sources } = playerGlobalState;
  const { videos } = sources as Sources;

  useEffect(() => {
    // player.player_.handleTechClick_ = () => false;

    // The icon follows the player's own paused() state rather than the name of the event that
    // announced the change: videojs-contrib-ads renames the events of the video element around
    // an ad break ('adpause' while an ad plays, 'contentpause' while the content comes back),
    // so a button bound to 'pause' alone stops hearing about pauses from the first ad on.
    const handlePlayStateChange = () => {
      if (player.paused()) {
        if (!hasEnded.current) setPlayState('pause');
        return;
      }
      // Playback is running again, so whatever had ended has been replayed or seeked away from.
      // Leaving the flag set is what froze the button: every later pause was ignored, so a tap
      // on the screen paused a video whose icon went on showing it as playing.
      hasEnded.current = false;
      setIsSeeking(false);
      setPlayState('play');
    };

    const handleEnd = () => {
      if (videos.find((item) => item.src === player.src())) {
        hasEnded.current = true;
        setPlayState('ended');
      }
    };

    const handleSeekBar = () => {
      hasEnded.current = false;
      handlePlayStateChange();
    };

    const handleSeeking = () => setIsSeeking(true);
    const handleSeeked = () => setIsSeeking(false);

    // `ended`, not `end`: the replay icon never appeared under the old spelling. Both names,
    // because an ad break renames the first one (see CONTENT_END_EVENTS).
    player.on(CONTENT_END_EVENTS, handleEnd);
    player.on(PLAY_STATE_EVENTS, handlePlayStateChange);
    player.on('seekbar', handleSeekBar);
    player.on('seeking', handleSeeking);
    player.on('seeked', handleSeeked);

    return () => {
      // Every listener is removed by its own reference: player.off('pause') without one drops
      // the pause listener of every other component and the redispatching of contrib-ads too.
      player.off(CONTENT_END_EVENTS, handleEnd);
      player.off(PLAY_STATE_EVENTS, handlePlayStateChange);
      player.off('seekbar', handleSeekBar);
      player.off('seeking', handleSeeking);
      player.off('seeked', handleSeeked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayVideo = () => {
    if (playState === 'ended') {
      const src = player.currentSrc();
      player.src(src);
      player.currentTime(0);
      player.load();
      player.play();
      hasEnded.current = false;
      setPlayState('play');
    } else if (player.paused()) {
      // What to do is read from the player, not from the icon: the icon is only a projection of
      // the player and a tap has to toggle what is actually playing even if the two ever drift.
      setPlayState('play');
      player.play();
    } else {
      setPlayState('pause');
      player.pause();
    }
  };

  const handleSkip = (skipTime) => {
    // if (player && !isSeeking) {
    clearTimeout(time.current);
    // time.current = null;
    const lastTime = player.currentTime();
    player.currentTime(lastTime + skipTime);

    time.current = setTimeout(() => {
      if (player.currentTime() === lastTime + skipTime) {
        player.currentTime(lastTime + skipTime + 1);
      }
    }, 1000);

    // setCount(count + 1);
    // const currentTime = player.currentTime();
    // clearTimeout(multipleRequest.current);
    // multipleRequest.current = setTimeout(() => {
    //   timerStatus.current = false;
    //   time.current = currentTime + (count + 1) * skipTime;
    //   player.currentTime(parseFloat(time.current));
    //   setCount(0);
    //   timerStatus.current = true;
    //   if (timerStatus.current) {
    //     setTimeout(() => {
    //       if (parseFloat(player.currentTime()) === parseFloat(time.current)) {
    //         player.currentTime(parseFloat(time.current) - 2);
    //       }
    //     }, 5000);
    //   }
    // }, 300);
    // }
  };

  // A short runs for seconds and is scrubbed by swiping, so a 15 second jump would clear most
  // of it. Only the play/pause button belongs there.
  const showSkip = playState !== 'ended' && !isVast && !isShort;

  return (
    <BigBtnFloatingContainer>
      {showSkip && (
        <BigBtnFloatingButton isPlay={false} onClick={() => handleSkip(+15)}>
          <AbsoluteCenter>
            <IconPlayForward />
          </AbsoluteCenter>
        </BigBtnFloatingButton>
      )}

      <BigPlayBtnFloatingButton isPlay={true} onClick={() => handlePlayVideo()}>
        <AbsoluteCenter>{getPlayStateIcon(playState)}</AbsoluteCenter>
      </BigPlayBtnFloatingButton>

      {showSkip && (
        <BigBtnFloatingButton isPlay={false} onClick={() => handleSkip(-15)}>
          <AbsoluteCenter>
            <IconFastRewind />
          </AbsoluteCenter>
        </BigBtnFloatingButton>
      )}
    </BigBtnFloatingContainer>
  );
};

export default PlayControls;

export const getPlayStateIcon = (playState) => {
  if (playState === 'pause') {
    return <IconPlayBig />;
  } else if (playState === 'play') {
    return <IconPause />;
  } else if (playState === 'ended') {
    return <IconReplay />;
  }
  return <IconPlayBig />;
};
